import * as React from 'react';
import { createContext, useState, FC, useCallback, useContext, useMemo, useEffect } from 'react';

const localStorageId = 'client_settings';

export interface ClientSettings {
  showCustomerNames: boolean;
  showMap: boolean;
};

const defaultClientSettings: ClientSettings = {
  showCustomerNames: false,
  showMap: false,
};

const getInitialClientSettings = () => {
  try {
    const rawSaved = localStorage.getItem(localStorageId);

    if(rawSaved == null) {
      return defaultClientSettings;
    }

    const saved = JSON.parse(rawSaved);

    return {
      ...defaultClientSettings,
      ...saved,
    };
  } catch {
    return defaultClientSettings;
  }
};

interface ClientSettingsConextContent {
  update?: (data: Partial<ClientSettings>) => void;
  data: ClientSettings;
};

const ClientSettingsContext = createContext<ClientSettingsConextContent>({ data: defaultClientSettings });

export const ClientSettingsProvider: FC = ({ children }) => {
  const [data, setData] = useState<ClientSettings>(getInitialClientSettings());

  const channel = useMemo(() => {
    if(BroadcastChannel == undefined) {
      return undefined;
    }

    const channel = new BroadcastChannel("client_settings");

    channel.onmessage = (event) => {
      setData(event.data as ClientSettings);
    };

    return channel;
  }, []);

  useEffect(() => {
    if(channel == null) {
      return;
    }

    return () => {
      channel?.close();
    };
  }, [channel]);

  const update = useCallback((newData: Partial<ClientSettings>) => {
    const merged = { ...data, ...newData };

    setData(merged);

    localStorage.setItem(localStorageId, JSON.stringify(merged));

    if(channel != null) {
      channel.postMessage(merged);
    }
  }, [data, setData]);

  const context: ClientSettingsConextContent = {
    data,
    update,
  };

  return (
  <ClientSettingsContext.Provider value={context}>
    {children}
  </ClientSettingsContext.Provider>
  );
};

export const useClientSettings = (): ClientSettings => {
  return useContext(ClientSettingsContext).data;
};

export const useClientSetting = function<T extends keyof ClientSettings>(key: T): ClientSettings[T] {
  return useContext(ClientSettingsContext).data[key];
};

export const useUpdateClientSettings = () => {
  return useContext(ClientSettingsContext).update;
}

